import React, { useState } from 'react';
import { toolkitApi } from '../../services/toolkit';

const inputCls =
  'w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 focus:border-emerald-500 focus:outline-none dark:border-slate-600 dark:bg-slate-800 dark:text-white';

function SentTracking({ message, onSaved }) {
  const [form, setForm] = useState({
    sent_date: message.sent_date || '',
    sent_method: message.sent_method || '',
    notes: message.notes || '',
  });
  const [saved, setSaved] = useState(false);
  const save = async () => {
    const patch = Object.fromEntries(
      Object.entries(form).filter(([, v]) => v !== ''),
    );
    const updated = await toolkitApi.updateMessage(message.id, patch);
    onSaved(updated);
    setSaved(true);
  };
  return (
    <div className="mt-4 rounded-xl border border-emerald-200 bg-emerald-50 p-4 dark:border-emerald-800 dark:bg-emerald-900/30">
      <p className="text-sm font-semibold text-emerald-900 dark:text-emerald-200">
        Keep your record straight
      </p>
      <p className="mb-3 text-xs text-emerald-800 dark:text-emerald-300">
        Save a copy of what you sent, the date, where you sent it, and any response you receive.
      </p>
      <div className="grid gap-3 sm:grid-cols-3">
        <label className="block text-xs font-medium text-slate-700 dark:text-slate-200">
          Date sent
          <input type="date" className={inputCls} value={form.sent_date}
            onChange={(e) => setForm({ ...form, sent_date: e.target.value })} />
        </label>
        <label className="block text-xs font-medium text-slate-700 dark:text-slate-200">
          How you sent it
          <select className={inputCls} value={form.sent_method}
            onChange={(e) => setForm({ ...form, sent_method: e.target.value })}>
            <option value="">Not sent yet</option>
            <option value="email">Email</option>
            <option value="portal">Tenant portal</option>
            <option value="mail">Printed / mail</option>
            <option value="in_person">Handed in person</option>
          </select>
        </label>
        <label className="block text-xs font-medium text-slate-700 dark:text-slate-200">
          Notes / response received
          <input type="text" className={inputCls} value={form.notes}
            onChange={(e) => setForm({ ...form, notes: e.target.value })} />
        </label>
      </div>
      <button type="button" onClick={save}
        className="mt-3 rounded-lg bg-emerald-600 px-4 py-1.5 text-xs font-semibold text-white hover:bg-emerald-700">
        {saved ? 'Saved ✓' : 'Save record'}
      </button>
    </div>
  );
}

export function MessageEditor({ message, onChange, onDone }) {
  const [subject, setSubject] = useState(message.subject);
  const [body, setBody] = useState(message.body);
  const [dirty, setDirty] = useState(false);
  const [copied, setCopied] = useState(false);
  const [exported, setExported] = useState(false);

  const isEmail = message.delivery_format === 'email';

  const save = async () => {
    const updated = await toolkitApi.updateMessage(message.id, { subject, body });
    onChange(updated);
    setDirty(false);
  };
  // Persist any edits before rendering the export server-side.
  const ensureSaved = async () => { if (dirty) await save(); };

  const copyEmail = async () => {
    await ensureSaved();
    const text = await toolkitApi.getEmailText(message.id);
    await navigator.clipboard.writeText(text);
    setCopied(true);
    setExported(true);
  };
  const copyBody = async () => {
    await ensureSaved();
    await navigator.clipboard.writeText(`${subject}\n\n${body}`);
    setCopied(true);
    setExported(true);
  };
  const downloadLetter = async () => {
    await ensureSaved();
    await toolkitApi.downloadDocx(message.id);
    setExported(true);
  };

  return (
    <div>
      <h2 className="mb-1 text-lg font-semibold text-slate-900 dark:text-white">
        {isEmail ? 'Your email' : 'Your letter'}
      </h2>
      <p className="mb-4 text-sm text-slate-600 dark:text-slate-300">
        Make it yours — edit anything below so it sounds like you.
        {!isEmail && ' The Word download adds your address block and date automatically.'}
      </p>
      <label className="mb-3 block">
        <span className="mb-1 block text-sm font-medium text-slate-700 dark:text-slate-200">Subject</span>
        <input className={inputCls} value={subject}
          onChange={(e) => { setSubject(e.target.value); setDirty(true); }} />
      </label>
      <label className="block">
        <span className="mb-1 block text-sm font-medium text-slate-700 dark:text-slate-200">Message</span>
        <textarea rows={14} className={inputCls} value={body}
          onChange={(e) => { setBody(e.target.value); setDirty(true); }} />
      </label>

      <div className="mt-4 flex flex-wrap items-center gap-2">
        {dirty && (
          <button type="button" onClick={save}
            className="rounded-lg bg-emerald-600 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-700">
            Save changes
          </button>
        )}
        {isEmail ? (
          <button type="button" onClick={copyEmail}
            className="rounded-lg border border-emerald-600 px-4 py-2 text-sm font-semibold text-emerald-700 hover:bg-emerald-50 dark:text-emerald-400 dark:hover:bg-emerald-900/30"
            title="Copies To, Subject and message — paste straight into your mail app">
            {copied ? 'Copied ✓' : 'Copy email'}
          </button>
        ) : (
          <>
            <button type="button" onClick={downloadLetter}
              className="rounded-lg border border-emerald-600 px-4 py-2 text-sm font-semibold text-emerald-700 hover:bg-emerald-50 dark:text-emerald-400 dark:hover:bg-emerald-900/30"
              title="A formal letter with your address block — opens in Word for final edits">
              Download Word letter
            </button>
            <button type="button" onClick={copyBody}
              className="rounded-lg border border-emerald-600 px-4 py-2 text-sm font-semibold text-emerald-700 hover:bg-emerald-50 dark:text-emerald-400 dark:hover:bg-emerald-900/30">
              {copied ? 'Copied ✓' : 'Copy text'}
            </button>
          </>
        )}
        {onDone && (
          <button type="button" onClick={onDone}
            className="ml-auto rounded-lg px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-800">
            Done
          </button>
        )}
      </div>

      {exported && <SentTracking message={message} onSaved={onChange} />}
    </div>
  );
}
